"use client";

import { useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { getCrmConfig, getCrmName, type CrmConfig } from "@/config/crm-logos";
import type { RolloutSyncPublic } from "@/convex/rollout/rolloutSyncState";

export interface CrmStatus {
  /** Underlying provider — "fub" (legacy direct integration) or "rollout" */
  provider: "fub" | "rollout" | null;
  /** The Rollout/internal app key for the connected CRM (e.g. "lofty") */
  appKey: string | null;
  /** Rollout credential id, if connected via Rollout */
  credentialId: string | null;
  /** True once status has been resolved */
  isLoaded: boolean;
  /** True if the CRM is connected via either path */
  isConnected: boolean;
  /** True if connected via legacy FUB OAuth */
  isFub: boolean;
  /** True if connected via Rollout */
  isRollout: boolean;
  /** True if Rollout is currently performing its initial sync */
  isSyncing: boolean;
  /** Last known sync timestamp (ms since epoch) */
  lastSyncedAt: number | null;
  /** Last known Rollout credential-callback sync state, if any */
  rolloutSync: RolloutSyncPublic | null;
  /** Full CRM display config (name + logo + bgColor) */
  crmConfig: CrmConfig | undefined;
  /** Convenience: friendly display name (or "CRM" fallback) */
  crmFriendlyName: string;
}

/**
 * Lightweight derived view of CRM connection status.
 *
 * Safe to mount on any page — only does two reactive queries and computes
 * everything via useMemo. No polling, no credential CRUD. For credential
 * management use `useRollout` from `./use-rollout`.
 */
export function useCrmStatus(): CrmStatus {
  const connectedIntegrations = useQuery(api.crmConnections.queries.getConnectedIntegrations, {});
  const rolloutConnectionStatus = useQuery(api.rollout.queries.getConnectionStatus);

  return useMemo<CrmStatus>(() => {
    const isLoaded = connectedIntegrations !== undefined && rolloutConnectionStatus !== undefined;

    if (!connectedIntegrations) {
      return {
        provider: null,
        appKey: null,
        credentialId: null,
        isLoaded,
        isConnected: false,
        isFub: false,
        isRollout: false,
        isSyncing: false,
        lastSyncedAt: null,
        rolloutSync: null,
        crmConfig: undefined,
        crmFriendlyName: "CRM",
      };
    }

    const provider = (connectedIntegrations.provider ?? null) as "fub" | "rollout" | null;
    const appKey = connectedIntegrations.appKey ?? null;
    const credentialId = connectedIntegrations.credentials?.credentialId ?? null;

    const isRollout = provider === "rollout";
    const isFub = provider === "fub";
    const isConnected =
      connectedIntegrations.status === "connected" || rolloutConnectionStatus?.connected === true;
    const isSyncing = Boolean(
      isRollout && rolloutConnectionStatus?.connected && rolloutConnectionStatus.syncStatus === "inProgress",
    );

    // Native FUB (legacy OAuth integration) leaves `appKey` unset on the team
    // record — it only sets `provider = "fub"`. For routing purposes we keep
    // the raw `appKey` untouched (other hooks rely on null meaning "not
    // Rollout-brokered"), but for the display name + logo we derive an
    // effective key so the UI shows "Follow Up Boss" instead of generic "CRM".
    const effectiveAppKey = appKey ?? (provider === "fub" ? "follow-up-boss" : null);

    return {
      provider,
      appKey,
      credentialId,
      isLoaded,
      isConnected,
      isFub,
      isRollout,
      isSyncing,
      lastSyncedAt: connectedIntegrations.lastSyncedAt ?? null,
      rolloutSync:
        rolloutConnectionStatus?.connected === true
          ? (rolloutConnectionStatus.rolloutSync ?? null)
          : null,
      crmConfig: getCrmConfig(effectiveAppKey),
      crmFriendlyName: getCrmName(effectiveAppKey),
    };
  }, [connectedIntegrations, rolloutConnectionStatus]);
}
